import { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, FileText, Building2, Wallet, Shield, Users, Award, Clock, TrendingUp, Landmark, Boxes, Scale, Quote, Star, ChevronLeft, ChevronRight } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { IMAGES } from '../constants/images';

// SVG Illustration Component for Hero
const HeroIllustration = () => (
  <svg viewBox="0 0 600 500" className="w-full h-full" fill="none" xmlns="http://www.w3.org/2000/svg">
    {/* Background Elements */}
    <defs>
      <linearGradient id="grad1" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="#3b5998" stopOpacity="0.1"/>
        <stop offset="100%" stopColor="#00bcd4" stopOpacity="0.1"/>
      </linearGradient>
      <linearGradient id="grad2" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="#3b5998"/>
        <stop offset="100%" stopColor="#00bcd4"/>
      </linearGradient>
    </defs>
    
    {/* Large Background Circle */}
    <circle cx="300" cy="250" r="200" fill="url(#grad1)" />
    
    {/* Building/Office Icon */}
    <g transform="translate(180, 120)">
      <rect x="0" y="80" width="240" height="180" rx="8" fill="#f8fafc" stroke="#e2e8f0" strokeWidth="2"/>
      <rect x="20" y="100" width="50" height="40" rx="4" fill="url(#grad2)" opacity="0.2"/>
      <rect x="90" y="100" width="50" height="40" rx="4" fill="url(#grad2)" opacity="0.3"/>
      <rect x="160" y="100" width="50" height="40" rx="4" fill="url(#grad2)" opacity="0.2"/>
      <rect x="20" y="160" width="50" height="40" rx="4" fill="url(#grad2)" opacity="0.3"/>
      <rect x="90" y="160" width="50" height="40" rx="4" fill="url(#grad2)" opacity="0.2"/>
      <rect x="160" y="160" width="50" height="40" rx="4" fill="url(#grad2)" opacity="0.3"/>
      <rect x="95" y="220" width="50" height="40" rx="4" fill="url(#grad2)"/>
    </g>
    
    {/* Floating Document */}
    <g transform="translate(80, 180)">
      <rect x="0" y="0" width="80" height="100" rx="6" fill="white" stroke="#e2e8f0" strokeWidth="2"/>
      <rect x="12" y="15" width="56" height="6" rx="3" fill="#3b5998" opacity="0.3"/>
      <rect x="12" y="30" width="40" height="6" rx="3" fill="#3b5998" opacity="0.2"/>
      <rect x="12" y="45" width="50" height="6" rx="3" fill="#3b5998" opacity="0.3"/>
      <rect x="12" y="60" width="35" height="6" rx="3" fill="#3b5998" opacity="0.2"/>
      <circle cx="55" cy="80" r="12" fill="url(#grad2)" opacity="0.8"/>
      <path d="M50 80 L53 83 L60 76" stroke="white" strokeWidth="2" fill="none"/>
    </g>
    
    {/* Floating Coins/Money */}
    <g transform="translate(450, 150)">
      <ellipse cx="40" cy="80" rx="35" ry="10" fill="#00bcd4" opacity="0.3"/>
      <ellipse cx="40" cy="70" rx="35" ry="10" fill="#00bcd4" opacity="0.4"/>
      <ellipse cx="40" cy="60" rx="35" ry="10" fill="#00bcd4" opacity="0.5"/>
      <ellipse cx="40" cy="50" rx="35" ry="10" fill="url(#grad2)"/>
      <text x="40" y="55" textAnchor="middle" fill="white" fontSize="14" fontWeight="bold">₹</text>
    </g>
    
    {/* Growth Chart */}
    <g transform="translate(420, 280)">
      <rect x="0" y="0" width="120" height="80" rx="8" fill="white" stroke="#e2e8f0" strokeWidth="2"/>
      <polyline points="15,60 35,45 55,50 75,30 95,20" stroke="url(#grad2)" strokeWidth="3" fill="none" strokeLinecap="round"/>
      <circle cx="95" cy="20" r="5" fill="#00bcd4"/>
    </g>
    
    {/* Person Silhouette */}
    <g transform="translate(100, 320)">
      <circle cx="30" cy="20" r="18" fill="url(#grad2)" opacity="0.8"/>
      <ellipse cx="30" cy="70" rx="25" ry="30" fill="url(#grad2)" opacity="0.6"/>
    </g>
    
    {/* Decorative Dots */}
    <circle cx="500" cy="100" r="6" fill="#00bcd4" opacity="0.4"/>
    <circle cx="520" cy="120" r="4" fill="#3b5998" opacity="0.3"/>
    <circle cx="150" cy="400" r="5" fill="#00bcd4" opacity="0.5"/>
    <circle cx="130" cy="380" r="3" fill="#3b5998" opacity="0.4"/>
    
    {/* Arrow pointing up */}
    <g transform="translate(350, 80)">
      <path d="M20 50 L20 10 M10 20 L20 10 L30 20" stroke="url(#grad2)" strokeWidth="4" fill="none" strokeLinecap="round"/>
    </g>
  </svg>
);

const Home = () => {
  const [activeTestimonialIndex, setActiveTestimonialIndex] = useState(0);
  const testimonialScrollRef = useRef(null);

  const testimonials = [
    {
      quote: "TOF Consultancy brought clarity and precision to our subsidy application process. Their in-depth knowledge of government schemes made a significant difference for our hospitality project.",
      name: "Vivek Choudhary",
      designation: "Director",
      company: "Holiday Inn Express Jaipur",
      image: IMAGES.vivek
    },
    {
      quote: "TOF Consultancy's expertise in financial solutions has greatly benefited our operations at Akshat Apartments Pvt. Ltd. Their guidance has been invaluable in navigating complex financial landscapes.",
      name: "Ved Prakash Sujaka",
      designation: "DGM - Finance",
      company: "Akshat Apartments Private Limited",
      image: IMAGES.ved
    },
    {
      quote: "TOF Consultancy's financial guidance was instrumental in navigating our growth as an NSE listed medical devices company and exporter. Their expertise is unmatched.",
      name: "Uday Chandra Jha",
      designation: "Senior Manager",
      company: "Poly Medicure Limited",
      image: IMAGES.uday
    },
    {
      quote: "We would like to thank team TOF for the work done for the organization. They are very clear what is required and made sure work was completed at the fastest pace in the shortest time.",
      name: "Sukhet Gupta",
      designation: "Vice President",
      company: "Shri Krishna Rolling Mills Limited",
      image: IMAGES.sukhet
    },
    {
      quote: "The team at TOF demonstrated exceptional professionalism in handling our financial requirements. Their guidance on RIPS subsidies helped us optimize our expansion plans.",
      name: "Mohit Soni",
      designation: "Director",
      company: "Soni Medicare Ltd",
      image: IMAGES.mohit
    },
    {
      quote: "Working with TOF has been a game-changer for our manufacturing unit. Their expertise in navigating government incentive schemes is truly commendable.",
      name: "Chetan M Mokzaria",
      designation: "Managing Director",
      company: "Minova Runaya Private Limited",
      image: IMAGES.chetan
    },
    {
      quote: "TOF's comprehensive approach to subsidy consultation helped us secure significant government benefits. Their team's dedication and expertise are outstanding.",
      name: "Basant Kumar Jain",
      designation: "Director",
      company: "Shri Adinath Cashew Pvt Ltd",
      image: IMAGES.basant
    },
    {
      quote: "The professional guidance from TOF Consultancy made our subsidy application process smooth and successful. Highly recommended for any business seeking government benefits.",
      name: "Aayush Kogta",
      designation: "Director",
      company: "Kogta Housing Development",
      image: IMAGES.aayush
    }
  ];

  const scrollToTestimonial = (index) => {
    if (testimonialScrollRef.current) {
      const cardWidth = 360;
      testimonialScrollRef.current.scrollTo({
        left: index * cardWidth,
        behavior: 'smooth'
      });
      setActiveTestimonialIndex(index);
    }
  };

  const handleTestimonialScroll = () => {
    if (testimonialScrollRef.current) {
      const scrollLeft = testimonialScrollRef.current.scrollLeft;
      const cardWidth = 360;
      const newIndex = Math.round(scrollLeft / cardWidth);
      setActiveTestimonialIndex(newIndex);
    }
  };

  useEffect(() => {
    const scrollElement = testimonialScrollRef.current;
    if (scrollElement) {
      scrollElement.addEventListener('scroll', handleTestimonialScroll);
      return () => scrollElement.removeEventListener('scroll', handleTestimonialScroll);
    }
  }, []);

  const services = [
    {
      icon: <FileText className="w-8 h-8" />,
      title: 'Government Subsidies',
      description: 'Expert guidance on state and central government incentive schemes for businesses and MSMEs.',
      link: '/services#subsidies'
    },
    {
      icon: <Building2 className="w-8 h-8" />,
      title: 'SME & Project Loans',
      description: 'Comprehensive financing solutions for new projects, business expansion, and working capital needs.',
      link: '/services#sme-loans'
    },
    {
      icon: <Wallet className="w-8 h-8" />,
      title: 'Retail Loans',
      description: 'Personal and business loans including home loans, LAP, and business financing from leading banks.',
      link: '/services#retail-loans'
    }
  ];

  const stats = [
    { number: '10+', label: 'Years Experience', icon: <Clock className="w-5 h-5" /> },
    { number: '500+', label: 'Clients Served', icon: <Users className="w-5 h-5" /> },
    { number: '₹1000Cr+', label: 'Funds Facilitated', icon: <Award className="w-5 h-5" /> },
    { number: '98%', label: 'Success Rate', icon: <Shield className="w-5 h-5" /> }
  ];

  const subsidySchemes = [
    { name: 'RIPS 2024', path: '/subsidies/rips-2024', icon: <Landmark className="w-6 h-6" /> },
    { name: 'PMEGP', path: '/subsidies/pmegp', icon: <Users className="w-6 h-6" /> },
    { name: 'VYUPY', path: '/subsidies/vyupy', icon: <Scale className="w-6 h-6" /> },
    { name: 'MSME Policy', path: '/subsidies/msme-policy-2024', icon: <Boxes className="w-6 h-6" /> },
    { name: 'Export Policy', path: '/subsidies/export-policy-2024', icon: <TrendingUp className="w-6 h-6" /> },
    { name: 'ODOP', path: '/subsidies/odop-policy-2024', icon: <Landmark className="w-6 h-6" /> },
  ];

  return (
    <div data-testid="home-page" className="overflow-hidden">
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center pt-20 bg-gradient-to-br from-white via-[#f8fafc] to-white" data-testid="hero-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 w-full">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-8 items-center">
            {/* Left Content */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
              className="order-2 lg:order-1"
            >
              <span className="inline-block px-4 py-2 bg-gradient-to-r from-[#3b5998]/10 to-[#00bcd4]/10 rounded-full text-sm font-medium text-[#3b5998] mb-6 border border-[#3b5998]/20">
                Trusted Since 2014
              </span>
              <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-semibold text-[#0f172a] leading-tight mb-6">
                Your Partner in
                <span className="gradient-text block">Financial Growth</span>
              </h1>
              <p className="text-lg text-[#64748b] leading-relaxed mb-8 max-w-lg">
                We provide end-to-end consultancy in Government Subsidies and Finance, 
                guiding you from documentation to disbursement with expertise and precision.
              </p>
              <div className="flex flex-wrap gap-4">
                <Link
                  to="/contact"
                  data-testid="hero-cta-primary"
                  className="gradient-btn px-8 py-4 rounded-full font-medium text-white inline-flex items-center gap-2 shadow-lg shadow-cyan-500/20"
                >
                  Get Started
                  <ArrowRight size={18} />
                </Link>
                <Link
                  to="/subsidies"
                  data-testid="hero-cta-secondary"
                  className="px-8 py-4 rounded-full font-medium text-[#0f172a] border-2 border-[#e2e8f0] hover:border-[#3b5998] transition-colors inline-flex items-center gap-2"
                >
                  Explore Subsidies
                </Link>
              </div>
            </motion.div>

            {/* Right Illustration */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
              className="order-1 lg:order-2 relative"
            >
              <div className="relative">
                <HeroIllustration />
                {/* Glass overlay card */}
                <div className="absolute bottom-4 left-4 right-4 glass rounded-xl p-5">
                  <p className="font-heading text-xl font-semibold text-[#0f172a] mb-1">D2D Model</p>
                  <p className="text-sm text-[#64748b]">Documentation to Disbursement — Complete end-to-end support</p>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-20 bg-white" data-testid="stats-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <motion.div
                key={stat.label}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="text-center"
              >
                <div className="inline-flex items-center justify-center w-12 h-12 bg-gradient-to-br from-[#3b5998] to-[#00bcd4] rounded-xl text-white mb-4">
                  {stat.icon}
                </div>
                <p className="stat-number text-4xl lg:text-5xl font-semibold mb-2">{stat.number}</p>
                <p className="text-[#64748b] text-sm font-medium">{stat.label}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Subsidy Schemes Quick Access */}
      <section className="py-20 bg-[#f8fafc]" data-testid="subsidies-quick-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <span className="inline-block px-4 py-2 bg-white rounded-full text-sm font-medium text-[#3b5998] mb-4 shadow-sm">
              Government Subsidies
            </span>
            <h2 className="font-heading text-3xl sm:text-4xl font-semibold text-[#0f172a] mb-4">
              Popular Subsidy Schemes
            </h2>
            <p className="text-[#64748b] max-w-2xl mx-auto">
              Access government benefits designed to support your business growth
            </p>
          </motion.div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            {subsidySchemes.map((scheme, index) => (
              <motion.div
                key={scheme.name}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: index * 0.05 }}
              >
                <Link
                  to={scheme.path}
                  className="block bg-white p-5 rounded-xl border border-slate-100 hover:border-[#00bcd4]/50 hover:shadow-lg transition-all text-center group"
                >
                  <div className="w-12 h-12 mx-auto mb-3 rounded-xl bg-gradient-to-br from-[#3b5998]/10 to-[#00bcd4]/10 flex items-center justify-center text-[#3b5998] group-hover:from-[#3b5998] group-hover:to-[#00bcd4] group-hover:text-white transition-all">
                    {scheme.icon}
                  </div>
                  <p className="font-medium text-[#0f172a] text-sm">{scheme.name}</p>
                </Link>
              </motion.div>
            ))}
          </div>

          <div className="text-center mt-8">
            <Link
              to="/subsidies"
              className="inline-flex items-center gap-2 text-[#3b5998] font-medium hover:text-[#00bcd4] transition-colors"
            >
              View All Schemes
              <ArrowRight size={18} />
            </Link>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-24 bg-white" data-testid="services-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <span className="inline-block px-4 py-2 bg-[#f1f5f9] rounded-full text-sm font-medium text-[#3b5998] mb-4">
              Our Services
            </span>
            <h2 className="font-heading text-3xl sm:text-4xl font-semibold text-[#0f172a] mb-4">
              Comprehensive Financial Solutions
            </h2>
            <p className="text-[#64748b] max-w-2xl mx-auto">
              From government subsidies to business loans, we offer tailored consultancy services 
              designed to accelerate your financial growth.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8">
            {services.map((service, index) => (
              <motion.div
                key={service.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.15 }}
              >
                <Card className="service-card h-full border border-slate-100 hover:border-[#00bcd4]/30 bg-white">
                  <CardContent className="p-8">
                    <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-[#3b5998] to-[#00bcd4] flex items-center justify-center text-white mb-6">
                      {service.icon}
                    </div>
                    <h3 className="font-heading text-xl font-semibold text-[#0f172a] mb-3">
                      {service.title}
                    </h3>
                    <p className="text-[#64748b] text-sm leading-relaxed mb-6">
                      {service.description}
                    </p>
                    <Link
                      to={service.link}
                      data-testid={`service-link-${index}`}
                      className="inline-flex items-center gap-2 text-[#3b5998] font-medium text-sm hover:text-[#00bcd4] transition-colors"
                    >
                      Learn More
                      <ArrowRight size={16} />
                    </Link>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="py-24 bg-[#0f172a]" data-testid="why-us-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <span className="inline-block px-4 py-2 bg-white/10 rounded-full text-sm font-medium text-[#00bcd4] mb-6">
                Why Choose Us
              </span>
              <h2 className="font-heading text-3xl sm:text-4xl font-semibold text-white mb-6">
                A Decade of Trust and Excellence
              </h2>
              <p className="text-slate-400 leading-relaxed mb-8">
                Since 2014, we have been committed to providing transparent, efficient, 
                and result-oriented financial consultancy services. Our D2D (Documentation to Disbursement) 
                model ensures complete support throughout your journey.
              </p>
              <ul className="space-y-4">
                {[
                  'Expert team of Chartered Accountants',
                  'Pan-India government subsidy expertise',
                  'Strong banking relationships',
                  'End-to-end documentation support',
                  'Transparent fee structure'
                ].map((item, index) => (
                  <li key={index} className="flex items-center gap-3 text-slate-300">
                    <span className="w-2 h-2 rounded-full bg-[#00bcd4]"></span>
                    {item}
                  </li>
                ))}
              </ul>
              <Link
                to="/leadership"
                data-testid="why-us-cta"
                className="inline-flex items-center gap-2 gradient-btn px-8 py-4 rounded-full font-medium text-white mt-8"
              >
                Meet Our Team
                <ArrowRight size={18} />
              </Link>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="relative"
            >
              <img
                src={IMAGES.foundersCover}
                alt="Our Founders"
                className="rounded-2xl shadow-2xl w-full object-cover"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-24 bg-[#f8fafc]" data-testid="home-testimonials-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <span className="inline-block px-4 py-2 bg-white rounded-full text-sm font-medium text-[#3b5998] mb-4 shadow-sm">
              Client Testimonials
            </span>
            <h2 className="font-heading text-3xl sm:text-4xl font-semibold text-[#0f172a] mb-4">
              What Our Clients Say
            </h2>
            <p className="text-[#64748b] max-w-2xl mx-auto">
              Real feedback from real clients who have experienced our services.
            </p>
          </motion.div>

          {/* Scrolling Testimonials */}
          <div className="relative">
            {/* Navigation Arrows */}
            <button
              onClick={() => scrollToTestimonial(Math.max(0, activeTestimonialIndex - 1))}
              className="absolute left-0 top-1/2 -translate-y-1/2 z-10 w-10 h-10 bg-white shadow-lg rounded-full flex items-center justify-center text-[#3b5998] hover:bg-[#3b5998] hover:text-white transition-colors hidden md:flex"
              aria-label="Previous testimonial"
            >
              <ChevronLeft size={20} />
            </button>
            <button
              onClick={() => scrollToTestimonial(Math.min(testimonials.length - 1, activeTestimonialIndex + 1))}
              className="absolute right-0 top-1/2 -translate-y-1/2 z-10 w-10 h-10 bg-white shadow-lg rounded-full flex items-center justify-center text-[#3b5998] hover:bg-[#3b5998] hover:text-white transition-colors hidden md:flex"
              aria-label="Next testimonial"
            >
              <ChevronRight size={20} />
            </button>

            {/* Scrollable Container */}
            <div
              ref={testimonialScrollRef}
              className="flex gap-6 overflow-x-auto scrollbar-hide pb-6 px-4 md:px-12 snap-x snap-mandatory"
              style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
            >
              {testimonials.map((testimonial, index) => (
                <motion.div
                  key={testimonial.name}
                  initial={{ opacity: 0, scale: 0.9 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className="flex-shrink-0 w-[320px] md:w-[360px] snap-center"
                >
                  <Card className="h-full bg-white border border-slate-100 hover:border-[#00bcd4]/30 hover:shadow-lg transition-all overflow-hidden">
                    <CardContent className="p-6">
                      {/* Profile Image & Info */}
                      <div className="flex items-start gap-4 mb-4">
                        {testimonial.image ? (
                          <img
                            src={testimonial.image}
                            alt={testimonial.name}
                            className="w-14 h-14 rounded-full object-cover border-2 border-[#00bcd4]"
                          />
                        ) : (
                          <div className="w-14 h-14 rounded-full bg-gradient-to-br from-[#3b5998] to-[#00bcd4] flex items-center justify-center text-white font-semibold text-lg flex-shrink-0">
                            {testimonial.name.split(' ').map(n => n[0]).join('').slice(0, 2)}
                          </div>
                        )}
                        <div className="flex-1">
                          <p className="font-semibold text-[#0f172a]">{testimonial.name}</p>
                          <p className="text-[#64748b] text-sm">{testimonial.designation}</p>
                          <p className="text-[#00bcd4] text-sm font-medium">{testimonial.company}</p>
                        </div>
                      </div>

                      {/* Quote */}
                      <div className="relative">
                        <Quote className="w-8 h-8 text-[#00bcd4]/20 absolute -top-2 -left-2" />
                        <blockquote className="text-[#64748b] leading-relaxed text-sm pl-6 line-clamp-4">
                          "{testimonial.quote}"
                        </blockquote>
                      </div>

                      {/* Rating */}
                      <div className="flex gap-1 mt-4">
                        {[...Array(5)].map((_, i) => (
                          <Star key={i} className="w-4 h-4 fill-[#00bcd4] text-[#00bcd4]" />
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>
              ))}
            </div>

            {/* Dots Indicator */}
            <div className="flex justify-center gap-2 mt-6">
              {testimonials.map((_, index) => (
                <button
                  key={index}
                  onClick={() => scrollToTestimonial(index)}
                  className={`w-2 h-2 rounded-full transition-all ${
                    index === activeTestimonialIndex ? 'bg-[#3b5998] w-6' : 'bg-slate-300'
                  }`}
                  aria-label={`Go to testimonial ${index + 1}`}
                />
              ))}
            </div>
          </div>

          <div className="text-center mt-8">
            <Link
              to="/clients"
              className="inline-flex items-center gap-2 text-[#3b5998] font-medium hover:text-[#00bcd4] transition-colors"
            >
              View All Testimonials
              <ArrowRight size={18} />
            </Link>
          </div>
        </div>
      </section>

      {/* Event Gallery Section - Auto-moving Carousel */}
      <section className="py-24 bg-white overflow-hidden" data-testid="gallery-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <span className="inline-block px-4 py-2 bg-[#f1f5f9] rounded-full text-sm font-medium text-[#3b5998] mb-4">
              Recent Events
            </span>
            <h2 className="font-heading text-3xl sm:text-4xl font-semibold text-[#0f172a] mb-4">
              Industry Engagements
            </h2>
            <p className="text-[#64748b] max-w-2xl mx-auto">
              Engaging with industry leaders and sharing insights on government subsidies and finance.
            </p>
          </motion.div>

          {/* Auto-moving Carousel */}
          <div className="relative">
            <div className="flex animate-scroll gap-6">
              {/* First set of images */}
              {[IMAGES.event1, IMAGES.event2, IMAGES.event3, IMAGES.event4, IMAGES.event5].map((image, index) => (
                <div key={index} className="flex-shrink-0 w-80 md:w-96">
                  <div className="rounded-2xl overflow-hidden shadow-lg h-64">
                    <img
                      src={image}
                      alt={`Event ${index + 1}`}
                      className="w-full h-full object-cover"
                    />
                  </div>
                </div>
              ))}
              {/* Duplicate for seamless loop */}
              {[IMAGES.event1, IMAGES.event2, IMAGES.event3, IMAGES.event4, IMAGES.event5].map((image, index) => (
                <div key={`dup-${index}`} className="flex-shrink-0 w-80 md:w-96">
                  <div className="rounded-2xl overflow-hidden shadow-lg h-64">
                    <img
                      src={image}
                      alt={`Event ${index + 1}`}
                      className="w-full h-full object-cover"
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="text-center mt-8">
            <a
              href="https://www.instagram.com/tofin.in/"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 text-[#3b5998] font-medium hover:text-[#00bcd4] transition-colors"
            >
              Follow us on Instagram for more updates
              <ArrowRight size={18} />
            </a>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-white" data-testid="cta-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="relative rounded-3xl bg-gradient-to-r from-[#3b5998] to-[#00bcd4] p-12 lg:p-16 overflow-hidden">
            <div className="absolute inset-0 opacity-10">
              <div className="absolute top-0 right-0 w-96 h-96 bg-white rounded-full blur-3xl -translate-y-1/2 translate-x-1/2"></div>
              <div className="absolute bottom-0 left-0 w-96 h-96 bg-white rounded-full blur-3xl translate-y-1/2 -translate-x-1/2"></div>
            </div>
            <div className="relative text-center">
              <h2 className="font-heading text-3xl sm:text-4xl font-semibold text-white mb-4">
                Ready to Unlock Your Financial Potential?
              </h2>
              <p className="text-white/80 max-w-2xl mx-auto mb-8">
                Schedule a consultation with our experts today and discover how we can help 
                you access government subsidies and optimal financing solutions.
              </p>
              <div className="flex flex-wrap justify-center gap-4">
                <Link
                  to="/contact"
                  data-testid="cta-contact"
                  className="px-8 py-4 bg-white text-[#3b5998] rounded-full font-medium hover:bg-white/90 transition-colors inline-flex items-center gap-2"
                >
                  Schedule Consultation
                  <ArrowRight size={18} />
                </Link>
                <a
                  href="tel:+917791093252"
                  data-testid="cta-call"
                  className="px-8 py-4 border-2 border-white text-white rounded-full font-medium hover:bg-white/10 transition-colors"
                >
                  Call Now
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
